import VectorLayer from 'ol/layer/Vector.js'
import GeoJSON from 'ol/format/GeoJSON.js'
import { transformExtent } from 'ol/proj.js'
import { fetchWithTimeout } from './ogcXml'
import { createProgressVectorSource } from './vectorProgressSource'

const CAPS_TIMEOUT_MS = 10000
const HIT_TOLERANCE_PX = 8

// Alias históricos de Web Mercator que ol/proj no reconoce directo como EPSG:3857.
const WKID_ALIASES = { 102100: 3857, 102113: 3857 }

function cleanBaseUrl(baseUrl) {
  return baseUrl.replace(/\/+$/, '')
}

// Mismo patrón que layers.js:238-258 para ArcGIS del catálogo: envelope + spatialRel intersects,
// reproyectado a 4326 (Esri REST acepta 4326 como inSR/outSR sin necesidad de conocer el wkid nativo).
function esriGeometryParams(extent, projection) {
  const [xmin, ymin, xmax, ymax] = transformExtent(extent, projection, 'EPSG:4326')
  return {
    geometry: JSON.stringify({ xmin, ymin, xmax, ymax, spatialReference: { wkid: 4326 } }),
    geometryType: 'esriGeometryEnvelope',
    spatialRel: 'esriSpatialRelIntersects',
    inSR: '4326'
  }
}

function extractEsriBBox(fullExtent) {
  if (!fullExtent) return null
  const { xmin, ymin, xmax, ymax, spatialReference } = fullExtent
  if (![xmin, ymin, xmax, ymax].every(Number.isFinite)) return null

  const rawWkid = spatialReference?.latestWkid || spatialReference?.wkid
  const wkid = WKID_ALIASES[rawWkid] || rawWkid
  if (!wkid || wkid === 4326) return [xmin, ymin, xmax, ymax]

  try {
    return transformExtent([xmin, ymin, xmax, ymax], `EPSG:${wkid}`, 'EPSG:4326')
  } catch (e) {
    return null
  }
}

export const esriRestConnector = {
  id: 'esri-rest',
  serviceType: 'Esri REST',
  label: 'Esri REST (ArcGIS)',

  matchesUrl(baseUrl) {
    return /\/(FeatureServer|MapServer)\b/i.test(baseUrl)
  },

  // Servicio REST propio de Esri (ArcGIS Online/Enterprise): no habla OGC, se consulta con
  // ?f=json para listar sublayers y con /{layerId}/query?f=geojson para los features.
  async detect(baseUrl) {
    const url = cleanBaseUrl(baseUrl)
    const res = await fetchWithTimeout(`${url}?f=json`, CAPS_TIMEOUT_MS)
    const contentType = res.headers.get('content-type') || ''
    if (!contentType.includes('json')) {
      throw new Error('El servidor no devolvió una respuesta JSON válida (¿es un servicio Esri REST?).')
    }

    const data = await res.json()
    if (data.error) {
      throw new Error(`Error del servidor: ${data.error.message || 'respuesta inválida.'}`)
    }

    // Caso 1: la URL apunta al servicio (FeatureServer/MapServer) — lista todas sus sublayers.
    if (Array.isArray(data.layers) && data.layers.length) {
      const bbox = extractEsriBBox(data.fullExtent)
      return data.layers.map(l => ({
        name: String(l.id),
        title: l.name || `Capa ${l.id}`,
        bbox,
        meta: { queryUrl: `${url}/${l.id}` }
      }))
    }

    // Caso 2: la URL ya apunta a una sublayer individual (ej. .../FeatureServer/5) — la
    // respuesta describe esa única capa (id, name, type, geometryType, fields...), sin "layers".
    if (data.type && data.geometryType) {
      const bbox = extractEsriBBox(data.extent)
      return [{
        name: String(data.id),
        title: data.name || `Capa ${data.id}`,
        bbox,
        meta: { queryUrl: url }
      }]
    }

    throw new Error('No se encontraron capas en el servicio.')
  },

  createOlLayer(layerConfig) {
    const queryBaseUrl = `${layerConfig.provider.meta.queryUrl}/query`
    const baseParams = { where: '1=1', outFields: '*', f: 'geojson', outSR: '4326' }

    const source = createProgressVectorSource({
      layerId: layerConfig.id,
      layerName: layerConfig.name,
      format: new GeoJSON(),

      // Filtradas por la extensión visible del mapa (strategy: bbox) — no se trae la capa completa.
      buildSingleShotUrl: ({ extent, projection }) =>
        `${queryBaseUrl}?${new URLSearchParams({ ...baseParams, ...esriGeometryParams(extent, projection) })}`,

      // Conteo previo vía returnCountOnly, acotado a la extensión visible. Si el servicio no lo
      // soporta o la respuesta no trae un count numérico, se cae al fallback de descarga única.
      async getTotalCount({ extent, projection, signal }) {
        const countUrl = `${queryBaseUrl}?${new URLSearchParams({ where: '1=1', returnCountOnly: 'true', f: 'json', ...esriGeometryParams(extent, projection) })}`
        const res = await fetchWithTimeout(countUrl, CAPS_TIMEOUT_MS, signal)
        const data = await res.json()
        return Number.isFinite(data.count) ? data.count : null
      },

      async fetchPage({ extent, projection, offset, pageSize, signal }) {
        const pageUrl = `${queryBaseUrl}?${new URLSearchParams({
          ...baseParams, ...esriGeometryParams(extent, projection),
          resultOffset: offset, resultRecordCount: pageSize
        })}`
        const res = await fetch(pageUrl, { signal })
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
        const data = await res.json()
        return data.features || []
      }
    })

    return new VectorLayer({
      source,
      opacity: layerConfig.opacity !== undefined ? layerConfig.opacity : 1,
      zIndex: 10
    })
  },

  queryFeatures({ layer, pixel, map, olLayer }) {
    if (!pixel || !map || !olLayer) return null

    const geoJSONFormat = new GeoJSON()
    const hitFeatures = []
    map.forEachFeatureAtPixel(pixel, (feature) => {
      hitFeatures.push(geoJSONFormat.writeFeatureObject(feature))
    }, {
      hitTolerance: HIT_TOLERANCE_PX,
      layerFilter: (l) => l === olLayer
    })

    return {
      layerName: layer.name,
      features: hitFeatures
    }
  }
}
